// Scored interviews, driver_rubric_v1. Migration 0265.
//
// Every question in Interview 1, Interview 2 and the reference calls is scored
// 0 to 3 against written anchors, and the rule in interview_decision_eval_v1
// decides the outcome. Nothing on this screen chooses pass or fail:
//   - Interview 1 records its outcome the moment its one scorer submits.
//   - Interview 2 has two scorers, the Driver Servant and Luyanda, and its
//     outcome is recorded by a button that can only record what the rule says.
//   - Interview 2 ends with a 15 minute drive the manager records alone, a pass
//     or fail gate beside the score (migration 6800). The second scorer can
//     sit an Interview 2 out, and it is then decided on one card (6801).
//
// The other interviewer's scores are sealed by the database until your own
// scorecard is submitted. This file never receives them before then, so it has
// nothing to hide.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, Car, CheckCircle2, ClipboardCheck, Lock, Phone, UserMinus, UserPlus, XCircle } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { Chip, type ChipTone } from './Chip'
import {
  useGetInterviewScoring,
  useSaveInterviewScore,
  useSubmitInterviewScorecard,
  useReviseInterviewScore,
  useRecordInterviewDecision,
  useAddInterviewReferee,
  useLogRefereeAttempt,
  useMarkRefereeUnreachable,
  useClassifyInterviewReferee,
  useRequireSecondReference,
  useReleaseReferenceHold,
  useSaveDriveScore,
  useSubmitDrive,
  useSitOutInterview,
} from '../../hooks/backend/interviews'
import { scorerNames } from '../../utils/today'
import {
  ReasonControl,
  RefereeForm,
  ReferenceRuleSummary,
  orList,
  pathLabel,
  shortDate,
  type RefereeFlags,
  type RefereeFormValues,
  type ReferenceRule,
  type RelationshipType,
} from './ReferenceRule'

type Stage = 'interview_1' | 'interview_2' | 'reference'

type RubricItem = {
  stage: Stage
  code: string
  part: string
  sort_order: number
  trait: number | null
  question: string
  school_version: string | null
  guidance: string | null
  anchor_3: string
  anchor_2: string
  anchor_1: string
  anchor_0: string
  min_score: number
  allows_na: boolean
  knockout_at_zero: boolean
  must_not_be_zero: boolean
  gate: 'truth' | 'reliability' | null
}

type RubricPart = {
  stage: Stage
  part: string
  label: string
  sort_order: number
  intro: string | null
  close_note: string | null
}

type Answer = { score: number | null; is_na: boolean; evidence: string | null }

type Totals = {
  total: number
  max: number
  pct: number | null
  items: number
  answered: number
  missing: string[]
  zero_codes: string[]
  must_zero: string[]
  needs_evidence: string[]
}

type Scorecard = {
  scorecard_id: string
  scorer_name: string
  mine: boolean
  status: 'draft' | 'submitted'
  submitted_at: string | null
  visible?: boolean
  answers: Record<string, Answer> | null
  totals: Totals | null
}

type InterviewBlock = {
  interview_id: string
  round: number
  stage: Stage
  manager_name: string | null
  // Migration 0270. Whether the viewer sits on this interview: the manager it
  // is booked with, or on Interview 2 the second interviewer. The database
  // refuses a score from anybody else, so the form is only offered when true.
  // Optional because a response from before 0270 does not carry it.
  mine?: boolean
  second_interviewer_name?: string | null
  scheduled_at: string | null
  completed_at: string | null
  canceled_at: string | null
  decision: { outcome: 'pass' | 'fail'; decided_at: string } | null
  scorecards: Scorecard[]
  // Migration 6801. 1 when the second scorer sat out and the interview is
  // decided on the manager's card alone; null means the rule's two.
  scorers_required?: number | null
  can_sit_out?: boolean
  sat_out?: { name: string | null; at: string; reason: string | null } | null
  // Migration 6800. Only on an Interview 2.
  drive?: DriveState | null
}

type DriveItem = {
  code: string
  sort_order: number
  question: string
  guidance: string | null
  anchor_3: string
  anchor_2: string
  anchor_1: string
  anchor_0: string
  knockout_at_zero: boolean
}

type DriveTotals = {
  total: number
  max: number
  pass_total: number
  missing: string[]
  knockouts: string[]
  needs_evidence: string[]
  pass: boolean
}

// interview_drive_view_v1. total, pass and knockouts are null while the viewer
// is the second scorer whose own card is not in (sealed).
type DriveState = {
  required: boolean
  status: 'not_required' | 'not_started' | 'draft' | 'submitted'
  label: string
  minutes?: number
  recorded_by?: string | null
  total?: number | null
  max?: number | null
  pass_total?: number
  knockouts?: string[] | null
  pass?: boolean | null
  sealed?: boolean
  mine?: boolean
  can_record?: boolean
  answers?: Record<string, { score: number | null; evidence: string | null }> | null
  totals?: DriveTotals | null
}

type Referee = {
  referee_id: string
  referee_name: string
  relationship: string
  phone: string
  kind: 'work' | 'school' | 'community'
  status: 'open' | 'completed' | 'unreachable'
  attempts: { at: string; by_name: string; note: string | null }[]
  scorecard: Scorecard | null
  // Migration 2800. Who the referee is to him. held_authority decides whether
  // this referee can be Reference A. classification_source 'backfill' or
  // 'legacy_form' marks a referee his manager may classify once.
  relationship_type?: string
  relationship_label?: string
  held_authority?: boolean
  capacity_note?: string | null
  organisation?: string | null
  known_from?: string | null
  known_to?: string | null
  classification_source?: 'form' | 'backfill' | 'legacy_form' | 'reclassified'
  flags?: RefereeFlags
}

type Evaluation = {
  outcome: 'awaiting_scores' | 'awaiting_references' | 'held_for_manager' | 'pass' | 'fail'
  reasons: string[]
  // Migration 2801. The reference rule as evaluated, under the policy in force
  // on the day Interview 2 was held.
  reference_rule?: ReferenceRule | null
  interview_2: {
    interview_id: string | null
    submitted: number
    sealed?: boolean
    average_pct?: number | null
    truth?: number | null
    reliability?: number | null
    knockouts?: string[]
    divergent?: { code: string; scores: number[] }[]
    decision?: { outcome: string } | null
    scorecards?: { scorer_name: string; totals: Totals }[]
  }
  references: { passed: number; failed: number; required: number }
  rules: { i1_pass_total: number; i2_pass_pct: number; gate_min: number; ref_pass_total: number }
}

type ScoringData = {
  // False when the login may not score at all, OR when this applicant is not
  // theirs (migration 0270). Applicant-level: it gates references and the
  // decision. Each round is further gated on its own `mine`.
  can_score: boolean
  is_mine?: boolean
  // Migration 0274. Reference calls are the applicant's manager's job: only the
  // manager on his most recent interview may log, close or score one. Anyone
  // on the applicant (can_score) may still ADD a referee. Absent on a response
  // from before 0274, where can_score was the whole answer.
  can_call_references?: boolean
  reference_caller?: string | null
  // Migration 2801. A scorer on his Interview 2, before its outcome is
  // recorded, may mark that a second reference is required.
  can_require_second?: boolean
  relationship_types?: RelationshipType[]
  me: string | null
  rubric: { version: string; parts: RubricPart[]; items: RubricItem[] }
  // Migration 6800. The drive checklist in force today.
  drive_rubric?: { version: string; pass_total: number; minutes: number; intro: string; items: DriveItem[] } | null
  interviews: InterviewBlock[]
  referees: Referee[]
  evaluation: Evaluation
}

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

const STAGE_TITLE: Record<Stage, string> = {
  interview_1: 'Interview 1: phone screen',
  interview_2: 'Interview 2: in person',
  reference: 'Reference call',
}

// ---------------------------------------------------------------------------
// One question
// ---------------------------------------------------------------------------

function QuestionRow({
  item,
  answer,
  readOnly,
  saving,
  onScore,
  onEvidence,
  evidencePlaceholder = 'What he said, in a line. Needed for a 0 or a 3.',
}: {
  item: RubricItem
  answer: Answer | undefined
  readOnly: boolean
  saving: boolean
  onScore: (score: number | null, isNa: boolean) => void
  onEvidence: (text: string) => void
  evidencePlaceholder?: string
}) {
  const [evidence, setEvidence] = useState(answer?.evidence ?? '')
  useEffect(() => setEvidence(answer?.evidence ?? ''), [answer?.evidence])

  const anchors: [number, string][] = [
    [3, item.anchor_3],
    [2, item.anchor_2],
    [1, item.anchor_1],
    [0, item.anchor_0],
  ]
  const selected = answer?.is_na ? 'na' : answer?.score ?? null
  const needsEvidence =
    !answer?.is_na && (answer?.score === 0 || answer?.score === 3) && !evidence.trim()

  return (
    <div className="rounded-md border border-border bg-background p-3">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 rounded bg-muted px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
          {item.code}
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-medium text-foreground">{item.question}</p>
          {item.school_version && (
            <p className="text-xs italic text-muted-foreground">School leaver: {item.school_version}</p>
          )}
          {item.guidance && <p className="text-xs text-muted-foreground">{item.guidance}</p>}
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {item.knockout_at_zero && <Chip tone="red">0 is a knockout</Chip>}
            {!item.knockout_at_zero && item.must_not_be_zero && <Chip tone="amber">0 fails this stage</Chip>}
            {item.gate && <Chip tone="brand">{item.gate === 'truth' ? 'Truth gate' : 'Reliability gate'}</Chip>}
          </div>
        </div>
        {saving && <Spinner className="h-4 w-4 shrink-0" />}
      </div>

      <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
        {anchors.map(([score, text]) => {
          const disabled = readOnly || score < item.min_score
          const active = selected === score
          return (
            <button
              key={score}
              type="button"
              disabled={disabled}
              onClick={() => onScore(score, false)}
              className={cn(
                'flex items-start gap-2 rounded-md border px-2.5 py-2 text-left text-xs transition-colors',
                active
                  ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-foreground'
                  : 'border-border bg-card text-muted-foreground hover:bg-accent',
                disabled && !active && 'cursor-not-allowed opacity-50 hover:bg-card',
              )}
            >
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold tabular-nums',
                  active ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))] text-white' : 'border-border',
                )}
              >
                {score}
              </span>
              <span>{text}</span>
            </button>
          )
        })}
        {item.allows_na && (
          <button
            type="button"
            disabled={readOnly}
            onClick={() => onScore(null, true)}
            className={cn(
              'rounded-md border px-2.5 py-2 text-left text-xs',
              selected === 'na'
                ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-foreground'
                : 'border-border bg-card text-muted-foreground hover:bg-accent',
            )}
          >
            N/A: no Uber profile. Left out of his maximum.
          </button>
        )}
      </div>

      <div className="mt-2">
        <Input
          value={evidence}
          disabled={readOnly}
          placeholder={evidencePlaceholder}
          onChange={(e) => setEvidence(e.target.value)}
          onBlur={() => {
            if ((answer?.evidence ?? '') !== evidence.trim()) onEvidence(evidence.trim())
          }}
          className={cn('h-8 text-xs', needsEvidence && 'border-amber-400')}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// A scorecard: parts, questions, running total, submit
// ---------------------------------------------------------------------------

function ScorecardForm({
  stage,
  data,
  interviewId,
  refereeId,
  card,
  revisable = false,
  onChanged,
}: {
  stage: Stage
  data: ScoringData
  interviewId: string | null
  refereeId: string | null
  card: Scorecard | null
  // A submitted Interview 2 card whose outcome is not yet recorded. Each answer
  // can then be changed, one at a time, with a note (migration 9200).
  revisable?: boolean
  onChanged: () => void
}) {
  const save = useSaveInterviewScore()
  const submit = useSubmitInterviewScorecard()
  const [answers, setAnswers] = useState<Record<string, Answer>>(card?.answers ?? {})
  const [totals, setTotals] = useState<Totals | null>(card?.totals ?? null)
  const [scorecardId, setScorecardId] = useState<string | null>(card?.scorecard_id ?? null)
  const [savingCode, setSavingCode] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setAnswers(card?.answers ?? {})
    setTotals(card?.totals ?? null)
    setScorecardId(card?.scorecard_id ?? null)
  }, [card?.scorecard_id, card?.status])

  // A submitted card has no local edits to lose, so it follows the server. This
  // is what shows a revised answer once the screen reloads after the revision.
  const submittedSignature = card?.status === 'submitted' ? JSON.stringify([card.answers, card.totals]) : null
  useEffect(() => {
    if (submittedSignature === null) return
    setAnswers(card?.answers ?? {})
    setTotals(card?.totals ?? null)
  }, [submittedSignature])

  const items = useMemo(
    () => data.rubric.items.filter((i) => i.stage === stage).sort((a, b) => a.sort_order - b.sort_order),
    [data.rubric.items, stage],
  )
  const parts = useMemo(
    () => data.rubric.parts.filter((p) => p.stage === stage).sort((a, b) => a.sort_order - b.sort_order),
    [data.rubric.parts, stage],
  )
  const readOnly = card?.status === 'submitted'

  const persist = useCallback(
    async (code: string, next: Answer) => {
      setAnswers((a) => ({ ...a, [code]: next }))
      setSavingCode(code)
      try {
        const res = (await save.trigger({
          interview_id: interviewId,
          referee_id: refereeId,
          code,
          score: next.score,
          is_na: next.is_na,
          evidence: next.evidence,
        })) as { scorecard_id: string; totals: Totals } | null
        if (res) {
          setScorecardId(res.scorecard_id)
          setTotals(res.totals)
        }
      } catch (err) {
        toast.error(`${code} was not saved`, { description: errText(err) })
      } finally {
        setSavingCode(null)
      }
    },
    [save.trigger, interviewId, refereeId],
  )

  const doSubmit = async () => {
    if (!scorecardId) return
    setSubmitting(true)
    try {
      const res = (await submit.trigger({ scorecard_id: scorecardId })) as { outcome?: string } | null
      if (stage === 'interview_1' && res?.outcome) {
        toast.success(res.outcome === 'pass' ? 'Interview 1 passed on the score' : 'Interview 1 did not pass on the score')
      } else {
        toast.success('Scorecard submitted')
      }
      setConfirming(false)
      onChanged()
    } catch (err) {
      toast.error('Scorecard not submitted', { description: errText(err) })
    } finally {
      setSubmitting(false)
    }
  }

  const missing = totals?.missing ?? items.map((i) => i.code)
  const needsEvidence = totals?.needs_evidence ?? []
  const ready = scorecardId !== null && missing.length === 0 && needsEvidence.length === 0

  return (
    <div className="space-y-4">
      {parts.map((p) => (
        <div key={p.part} className="space-y-2">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{p.label}</h5>
          {p.intro && (
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">{p.intro}</p>
          )}
          {items
            .filter((i) => i.part === p.part)
            .map((item) => (
              <div key={item.code} className="space-y-1">
                <QuestionRow
                  item={item}
                  answer={answers[item.code]}
                  readOnly={readOnly}
                  saving={savingCode === item.code}
                  onScore={(score, isNa) =>
                    persist(item.code, { score, is_na: isNa, evidence: answers[item.code]?.evidence ?? null })
                  }
                  onEvidence={(text) =>
                    persist(item.code, {
                      score: answers[item.code]?.score ?? null,
                      is_na: answers[item.code]?.is_na ?? false,
                      evidence: text || null,
                    })
                  }
                />
                {readOnly && revisable && card && (
                  <ReviseAnswer
                    scorecardId={card.scorecard_id}
                    item={item}
                    answer={answers[item.code]}
                    onChanged={onChanged}
                  />
                )}
              </div>
            ))}
          {p.close_note && (
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Close: </span>
              {p.close_note}
            </p>
          )}
        </div>
      ))}

      <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card p-3 shadow-sm">
        <div className="text-sm">
          <span className="font-semibold tabular-nums">
            {totals ? `${totals.total} of ${totals.max}` : `0 of ${items.length * 3}`}
          </span>
          {totals?.pct != null && <span className="ml-1 text-muted-foreground">({totals.pct}%)</span>}
          <span className="ml-2 text-xs text-muted-foreground">
            {missing.length > 0 ? `${missing.length} still to score` : 'Every question scored'}
            {needsEvidence.length > 0 ? `. Note needed on ${needsEvidence.join(', ')}` : ''}
          </span>
        </div>
        {!readOnly && (
          <Button variant="brand" disabled={!ready || submitting} onClick={() => setConfirming(true)}>
            <ClipboardCheck className="h-4 w-4" /> Submit scorecard
          </Button>
        )}
        {readOnly && <Chip tone="green">Submitted</Chip>}
      </div>

      <Dialog open={confirming} onOpenChange={(o) => !o && !submitting && setConfirming(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit this scorecard?</DialogTitle>
            <DialogDescription>
              {stage === 'interview_1'
                ? `Submitting records the outcome of Interview 1 from the score: ${data.evaluation.rules.i1_pass_total} of 30 or more, with no 0 on a must pass question. It cannot be changed afterwards.`
                : stage === 'interview_2'
                  ? 'Your scores lock when you submit. The other interviewer\'s scores open to you once yours is in. Until the outcome is recorded you can still change an answer, with a written note, and the old score is kept.'
                  : 'The reference scores lock when you submit.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={submitting}>
              Keep scoring
            </Button>
            <Button variant="brand" onClick={doSubmit} disabled={submitting}>
              {submitting && <Spinner className="h-4 w-4" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// One answer on a submitted Interview 2 card, changed after the fact. The note
// is required because the revision is kept beside the old score, and the next
// reader needs to know why it moved.
function ReviseAnswer({
  scorecardId,
  item,
  answer,
  onChanged,
}: {
  scorecardId: string
  item: RubricItem
  answer: Answer | undefined
  onChanged: () => void
}) {
  const revise = useReviseInterviewScore()
  const [open, setOpen] = useState(false)
  const [score, setScore] = useState<number>(answer?.score ?? 2)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const doRevise = async () => {
    setSaving(true)
    try {
      await revise.trigger({ scorecard_id: scorecardId, code: item.code, score, evidence: note })
      toast.success(`${item.code} changed to ${score}`)
      setOpen(false)
      setNote('')
      onChanged()
    } catch (err) {
      toast.error(`${item.code} was not changed`, { description: errText(err) })
    } finally {
      setSaving(false)
    }
  }

  if (!open) {
    return (
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setScore(answer?.score ?? 2)
            setOpen(true)
          }}
        >
          Change this answer
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5 rounded-md border border-border bg-muted p-2">
      <span className="text-xs text-muted-foreground">
        {item.code} from {answer?.is_na ? 'N/A' : answer?.score ?? 'no score'} to
      </span>
      <NativeSelect value={score} onChange={(e) => setScore(Number(e.target.value))} className="h-7 w-14 text-xs">
        {[3, 2, 1, 0]
          .filter((s) => s >= item.min_score)
          .map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
      </NativeSelect>
      <Input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Why it changes: what he said"
        className="h-7 w-56 text-xs"
      />
      <Button size="sm" variant="brand" disabled={!note.trim() || saving} onClick={doRevise}>
        {saving && <Spinner className="h-3.5 w-3.5" />}
        Save
      </Button>
      <Button size="sm" variant="ghost" disabled={saving} onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </div>
  )
}

// A table cell holds the start of a question, cut at a word so it never ends
// mid word. The full question is one click away in the scorecard above.
function shortQuestion(q: string, max = 70): string {
  if (q.length <= max) return q
  const cut = q.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return `${cut.slice(0, space > 40 ? space : max)}…`
}

// ---------------------------------------------------------------------------
// Interview 2: both scorecards side by side, and the revision of a split
// ---------------------------------------------------------------------------

function CompareTable({
  data,
  block,
  onChanged,
}: {
  data: ScoringData
  block: InterviewBlock
  onChanged: () => void
}) {
  const revise = useReviseInterviewScore()
  const [open, setOpen] = useState<string | null>(null)
  const [score, setScore] = useState<number>(2)
  const [note, setNote] = useState('')
  const cards = block.scorecards.filter((c) => c.status === 'submitted' && c.visible !== false)
  if (cards.length < 2) return null
  const mine = cards.find((c) => c.mine)
  const divergent = new Set((data.evaluation.interview_2.divergent ?? []).map((d) => d.code))
  const items = data.rubric.items.filter((i) => i.stage === 'interview_2')
  const decided = !!block.decision

  const doRevise = async (code: string) => {
    if (!mine) return
    try {
      await revise.trigger({ scorecard_id: mine.scorecard_id, code, score, evidence: note })
      toast.success(`${code} revised`)
      setOpen(null)
      setNote('')
      onChanged()
    } catch (err) {
      toast.error('Not revised', { description: errText(err) })
    }
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-xs">
        <thead className="bg-muted text-muted-foreground">
          <tr>
            <th className="px-2 py-1.5 text-left">Question</th>
            {cards.map((c) => (
              <th key={c.scorecard_id} className="px-2 py-1.5 text-center">
                {c.scorer_name}
              </th>
            ))}
            <th className="px-2 py-1.5" />
          </tr>
        </thead>
        <tbody>
          {items.map((i) => {
            const split = divergent.has(i.code)
            return (
              <tr key={i.code} className={cn('border-t border-border', split && 'bg-amber-50 dark:bg-amber-950')}>
                <td className="px-2 py-1.5">
                  <span className="font-semibold">{i.code}</span>{' '}
                  <span className="text-muted-foreground">{shortQuestion(i.question)}</span>
                </td>
                {cards.map((c) => {
                  const a = c.answers?.[i.code]
                  return (
                    <td key={c.scorecard_id} className="px-2 py-1.5 text-center tabular-nums" title={a?.evidence ?? ''}>
                      {a?.is_na ? 'N/A' : a?.score ?? ''}
                    </td>
                  )
                })}
                <td className="px-2 py-1.5 text-right">
                  {mine && !decided && (
                    open === i.code ? (
                      <div className="flex items-center gap-1">
                        <NativeSelect
                          value={score}
                          onChange={(e) => setScore(Number(e.target.value))}
                          className="h-7 w-14 text-xs"
                        >
                          {[3, 2, 1, 0].map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </NativeSelect>
                        <Input
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="The note it rests on"
                          className="h-7 w-40 text-xs"
                        />
                        <Button size="sm" variant="brand" disabled={!note.trim()} onClick={() => doRevise(i.code)}>
                          Save
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setOpen(i.code)
                          setScore(mine.answers?.[i.code]?.score ?? 2)
                        }}
                      >
                        Revise mine
                      </Button>
                    )
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {divergent.size > 0 && !decided && (
        <p className="border-t border-border px-2 py-1.5 text-xs text-muted-foreground">
          Rows in amber are 2 or more apart. Each of you reads his note aloud. Keep your score, or revise it with the
          note it rests on. If you still disagree, both scores stand and are averaged. Any other answer of yours can
          also be revised, with a note, until the outcome is recorded.
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// References
// ---------------------------------------------------------------------------

function ReferencesSection({
  applicationId,
  data,
  onChanged,
}: {
  applicationId: string
  data: ScoringData
  onChanged: () => void
}) {
  const add = useAddInterviewReferee()
  const attempt = useLogRefereeAttempt()
  const unreachable = useMarkRefereeUnreachable()
  const classify = useClassifyInterviewReferee()
  const requireSecond = useRequireSecondReference()
  const releaseHold = useReleaseReferenceHold()
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [classifyId, setClassifyId] = useState<string | null>(null)

  const types = data.relationship_types ?? []
  const rule = data.evaluation.reference_rule
  const conditional = rule?.rule_kind === 'conditional' ? rule : null
  const decided = !!data.interviews.find((b) => b.stage === 'interview_2' && !b.canceled_at)?.decision

  const doAdd = async (v: RefereeFormValues) => {
    setBusy(true)
    try {
      const res = (await add.trigger({
        application_id: applicationId,
        referee_name: v.referee_name,
        phone: v.phone,
        consent: v.consent,
        relationship_type: v.relationship_type,
        capacity_note: v.capacity_note,
        organisation: v.organisation || null,
        known_from: v.known_from || null,
        known_to: v.known_to || null,
      })) as { flags?: RefereeFlags } | null
      const shared = res?.flags?.coordination ?? []
      const inside = res?.flags?.inside_business ?? []
      if (shared.length > 0) {
        toast.warning('Referee added, and flagged', {
          description: `This number was also given as a referee by ${shared
            .map((c) => c.other_applicant_name ?? 'another applicant')
            .join(', ')}. A second reference is now required.`,
        })
      } else if (inside.length > 0) {
        toast.warning('Referee added, and flagged', {
          description: `This number belongs to ${inside.map((p) => p.person_name ?? 'someone').join(', ')}, inside the business.`,
        })
      } else {
        toast.success('Referee added')
      }
      setAdding(false)
      onChanged()
    } catch (err) {
      toast.error('Referee not added', { description: errText(err) })
    } finally {
      setBusy(false)
    }
  }

  const doClassify = async (refereeId: string, v: RefereeFormValues) => {
    setBusy(true)
    try {
      await classify.trigger({
        referee_id: refereeId,
        relationship_type: v.relationship_type,
        capacity_note: v.capacity_note,
        organisation: v.organisation || null,
        known_from: v.known_from || null,
        known_to: v.known_to || null,
      })
      toast.success('Referee classified')
      setClassifyId(null)
      onChanged()
    } catch (err) {
      toast.error('Not classified', { description: errText(err) })
    } finally {
      setBusy(false)
    }
  }

  const withReason = async (fn: () => Promise<unknown>, ok: string): Promise<boolean> => {
    setBusy(true)
    try {
      await fn()
      toast.success(ok)
      onChanged()
      return true
    } catch (err) {
      toast.error('Not recorded', { description: errText(err) })
      return false
    } finally {
      setBusy(false)
    }
  }

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn()
      toast.success(ok)
      onChanged()
    } catch (err) {
      toast.error('Not recorded', { description: errText(err) })
    }
  }

  const refs = data.referees
  const refPart = data.rubric.parts.find((p) => p.stage === 'reference')
  const mayCall = data.can_call_references ?? data.can_score

  const headline = conditional
    ? conditional.reference_b.status === 'not_yet_known'
      ? 'One required, a second not yet known'
      : conditional.required === 1
        ? 'One reference required'
        : 'Two references required'
    : `${data.evaluation.references.passed} of ${data.evaluation.references.required} passed`

  return (
    <div className="space-y-3" data-testid="reference-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">Reference calls</h4>
        <span className="text-xs text-muted-foreground">
          {headline}
          {data.evaluation.references.failed > 0 ? `, ${data.evaluation.references.failed} below standard` : ''}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {conditional
          ? `One reference from someone who supervised, employed, taught, coached or led him, ${data.evaluation.rules.ref_pass_total} of 30 or better with no 0 on ${orList(conditional.policy.reference_knockout_items)}. A second only where the rule calls for it, from anyone but the same person. Family and friends can be the second, never the first.`
          : `Two people who supervised his work. Each call must reach ${data.evaluation.rules.ref_pass_total} of 30 with no 0 on R0, R6b, R7 or R8.`}
      </p>

      <ReferenceRuleSummary rule={rule} />

      {conditional?.unreachable.held && (
        <div
          className="space-y-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100"
          data-testid="unreachable-hold"
        >
          <p>
            <span className="font-medium">Held for {conditional.unreachable.caller}.</span> {conditional.unreachable.count} referees could not be
            reached. Nothing is recorded until his manager decides whether the reference calls go on.
          </p>
          {mayCall && !decided && (
            <ReasonControl
              testId="release-hold"
              buttonLabel="Let the calls go on"
              title="Why should the reference calls go on?"
              note="This passes nothing. A replacement referee still has to be reached and scored."
              placeholder="He gave a third referee in person, his former shift supervisor."
              confirmLabel="Release the hold"
              busy={busy}
              onSubmit={(reason) =>
                withReason(() => releaseHold.trigger({ application_id: applicationId, reason }), 'The reference calls go on')
              }
            />
          )}
        </div>
      )}

      {conditional && conditional.discretion.length > 0 && (
        <div className="space-y-0.5 text-xs text-muted-foreground" data-testid="discretion-list">
          {conditional.discretion.map((d) => (
            <p key={d.at}>
              <span className="font-medium text-foreground">{d.by_name ?? 'An interviewer'}</span> asked for a second reference on{' '}
              {shortDate(d.at)}: {d.reason}
            </p>
          ))}
        </div>
      )}

      {conditional && data.can_require_second && (
        <ReasonControl
          testId="require-second"
          buttonLabel="Second reference required"
          title="Why does he need a second reference?"
          note="This only ever adds a call. Once recorded it cannot be taken back, by you or anyone else."
          placeholder="His account of leaving his last job did not match the dates he gave."
          confirmLabel="Require a second reference"
          busy={busy}
          onSubmit={(reason) =>
            withReason(
              () => requireSecond.trigger({ application_id: applicationId, reason }),
              'A second reference is now required',
            )
          }
        />
      )}

      {refs.map((r) => {
        const tone: ChipTone = r.status === 'completed' ? 'green' : r.status === 'unreachable' ? 'neutral' : 'amber'
        const days = new Set(r.attempts.map((a) => new Date(a.at).toDateString())).size
        const known =
          r.known_from || r.known_to
            ? `Known ${r.known_from ? `from ${shortDate(r.known_from)}` : ''}${r.known_to ? ` to ${shortDate(r.known_to)}` : r.known_from ? ' to now' : ''}`
            : ''
        const mayClassify =
          mayCall && !decided && (r.classification_source === 'backfill' || r.classification_source === 'legacy_form')
        const shared = r.flags?.coordination ?? []
        const inside = r.flags?.inside_business ?? []
        return (
          <div key={r.referee_id} className="rounded-md border border-border bg-card p-3" data-testid="referee-card">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 space-y-0.5">
                <p className="text-sm font-medium text-foreground">
                  {r.referee_name}{' '}
                  <span className="text-muted-foreground">· {r.relationship_label ?? r.relationship}</span>
                </p>
                {r.capacity_note && (
                  <p className="text-xs text-muted-foreground">
                    "{r.capacity_note}"{r.organisation ? `, ${r.organisation}` : ''}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  <span className="whitespace-nowrap">
                    <Phone className="mr-1 inline h-3 w-3 align-[-2px]" />
                    {r.phone}
                  </span>
                  {known && ` · ${known}`}
                  {r.attempts.length > 0 && ` · ${r.attempts.length} missed call(s) on ${days} day(s)`}
                </p>
                <div className="flex flex-wrap gap-1 pt-0.5">
                  {r.held_authority === true && <Chip tone="brand">Can be the first reference</Chip>}
                  {r.held_authority === false && <Chip tone="neutral">Second reference only</Chip>}
                  {mayClassify && <Chip tone="amber">Recorded before the type existed</Chip>}
                  {shared.length > 0 && (
                    <Chip tone="amber" className="whitespace-normal text-left">
                      Number also given by {shared.map((c) => c.other_applicant_name ?? 'another applicant').join(', ')}
                    </Chip>
                  )}
                  {inside.length > 0 && (
                    <Chip tone="neutral" className="whitespace-normal text-left">
                      Inside the business: {inside.map((p) => p.person_name ?? 'someone').join(', ')}
                    </Chip>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {r.scorecard?.totals && r.scorecard.status === 'submitted' && (
                  <span className="text-xs font-semibold tabular-nums">{r.scorecard.totals.total} of 30</span>
                )}
                <Chip tone={tone}>{r.status === 'open' ? 'Open' : r.status === 'completed' ? 'Scored' : 'Unreachable'}</Chip>
              </div>
            </div>
            {mayClassify && classifyId !== r.referee_id && (
              <div className="mt-2">
                <Button size="sm" variant="outline" onClick={() => setClassifyId(r.referee_id)}>
                  Say who this referee is to him
                </Button>
              </div>
            )}
            {classifyId === r.referee_id && (
              <div className="mt-2">
                <RefereeForm
                  types={types}
                  mode="classify"
                  initial={{ capacity_note: r.capacity_note ?? '', organisation: r.organisation ?? '' }}
                  busy={busy}
                  onSubmit={(v) => doClassify(r.referee_id, v)}
                  onCancel={() => setClassifyId(null)}
                />
              </div>
            )}
            {r.status === 'open' && mayCall && (
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" variant="brand" onClick={() => setOpenId(openId === r.referee_id ? null : r.referee_id)}>
                  {openId === r.referee_id ? 'Hide the call' : 'Score the call'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => run(() => attempt.trigger({ referee_id: r.referee_id, note: 'No answer' }), 'Missed call logged')}
                >
                  Log missed call
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={days < 2}
                  title={days < 2 ? 'Needs missed calls on two different days' : ''}
                  onClick={() => run(() => unreachable.trigger({ referee_id: r.referee_id }), 'Marked unreachable. Add another referee.')}
                >
                  Mark unreachable
                </Button>
              </div>
            )}
            {(openId === r.referee_id || r.status === 'completed') && (
              <div className="mt-3">
                {refPart?.intro && openId === r.referee_id && (
                  <p className="mb-2 text-xs text-muted-foreground">
                    Before dialling, have his C5, C6 and C7 answers from Interview 2 in front of you.
                  </p>
                )}
                <ScorecardForm
                  stage="reference"
                  data={data}
                  interviewId={null}
                  refereeId={r.referee_id}
                  card={r.scorecard}
                  onChanged={onChanged}
                />
              </div>
            )}
          </div>
        )
      })}

      {!mayCall && (
        <p className="text-xs text-muted-foreground" data-testid="reference-caller">
          {data.reference_caller
            ? `Reference calls are made and scored by ${data.reference_caller}, his manager.`
            : 'Reference calls are made and scored by his manager, and nobody is assigned yet.'}
          {data.can_score ? ' You can add a referee you hear about, and the call is theirs.' : ''}
        </p>
      )}

      {data.can_score &&
        (adding ? (
          <RefereeForm types={types} mode="add" busy={busy} onSubmit={doAdd} onCancel={() => setAdding(false)} />
        ) : (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="add-referee">
            <UserPlus className="h-4 w-4" /> Add referee
          </Button>
        ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The decision
// ---------------------------------------------------------------------------

function DecisionSection({
  applicationId,
  data,
  onChanged,
}: {
  applicationId: string
  data: ScoringData
  onChanged: () => void
}) {
  const record = useRecordInterviewDecision()
  const [seat, setSeat] = useState(false)
  const [busy, setBusy] = useState(false)
  const ev = data.evaluation
  const i2 = ev.interview_2
  const decided = i2.decision

  const tone: ChipTone =
    ev.outcome === 'pass' ? 'green' : ev.outcome === 'fail' ? 'red' : 'amber'

  // Migration 2801. The reference line states the path and the rule it was
  // judged under. A recorded decision carries its own; an open one shows what
  // the rule says now. The figures are the evaluation's, never counted here.
  const rule = ev.reference_rule
  const recorded = decided as (typeof decided & { reference_path?: string | null; reference_policy_version?: string | null }) | null
  // A decision recorded before 2801 carries no policy: it was the flat rule.
  // One recorded after it with no path was decided before the references.
  const refPath = recorded
    ? recorded.reference_path ?? (recorded.reference_policy_version ? 'not_reached' : 'flat_rule')
    : rule?.path ?? null
  const refPolicy = recorded
    ? recorded.reference_policy_version ?? 'ref_policy_v1_two_flat'
    : rule?.policy_version ?? null
  const refStat = (() => {
    if (rule?.rule_kind === 'conditional') {
      const a = rule.reference_a.status === 'met'
      const b = rule.reference_b
      const done = (a ? 1 : 0) + (b.status === 'required' && b.met ? 1 : 0)
      const need = b.status === 'not_yet_known' ? '1, a 2nd not yet known' : `${rule.required ?? 2} passed`
      return {
        value: `${done} of ${rule.required ?? '1 or 2'}`,
        ok: a && (b.status === 'not_required' || (b.status === 'required' && b.met)) && ev.references.failed === 0,
        need,
      }
    }
    return {
      value: `${ev.references.passed} of ${ev.references.required}`,
      ok: ev.references.passed >= ev.references.required && ev.references.failed === 0,
      need: `${ev.references.required} passed`,
    }
  })()
  const label =
    ev.outcome === 'pass'
      ? 'Hire: every rule met'
      : ev.outcome === 'fail'
        ? 'No offer: the rule is not met'
        : ev.outcome === 'awaiting_references'
          ? 'Waiting for reference calls'
          : ev.outcome === 'held_for_manager'
            ? 'Held for his manager: referees unreachable'
            : 'Waiting for Interview 2 scores'

  const doRecord = async () => {
    setBusy(true)
    try {
      const res = (await record.trigger({ application_id: applicationId, seat_confirmed: seat })) as {
        outcome: string
      } | null
      toast.success(res?.outcome === 'pass' ? 'Offer recorded. The offer email goes out.' : 'Outcome recorded: no offer.')
      onChanged()
    } catch (err) {
      toast.error('Outcome not recorded', { description: errText(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">The decision</h4>
        {decided ? (
          <Chip tone={decided.outcome === 'pass' ? 'green' : 'red'}>
            Recorded: {decided.outcome === 'pass' ? 'offer' : 'no offer'}
          </Chip>
        ) : (
          <Chip tone={tone}>{label}</Chip>
        )}
      </div>

      {i2.sealed ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5" /> Scores are sealed until your own Interview 2 scorecard is submitted.
        </p>
      ) : (
        i2.submitted >= 2 && (
          <div className="grid gap-2 text-xs sm:grid-cols-4">
            <Stat label="Average" value={i2.average_pct != null ? `${i2.average_pct}%` : ''} ok={(i2.average_pct ?? 0) >= ev.rules.i2_pass_pct} need={`${ev.rules.i2_pass_pct}%`} />
            <Stat label="Truth" value={i2.truth != null ? String(i2.truth) : ''} ok={(i2.truth ?? 0) >= ev.rules.gate_min} need={String(ev.rules.gate_min)} />
            <Stat label="Reliability" value={i2.reliability != null ? String(i2.reliability) : ''} ok={(i2.reliability ?? 0) >= ev.rules.gate_min} need={String(ev.rules.gate_min)} />
            <Stat label="References" value={refStat.value} ok={refStat.ok} need={refStat.need} />
          </div>
        )
      )}

      {(refPolicy || refPath) && (
        <p className="text-xs text-muted-foreground" data-testid="decision-reference-line">
          <span className="font-medium text-foreground">References: </span>
          {pathLabel(refPath)}
          {refPolicy ? `. Rule ${refPolicy}.` : '.'}
        </p>
      )}

      {ev.reasons.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
          {ev.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}

      {!decided && data.can_score && (ev.outcome === 'pass' || ev.outcome === 'fail') && (
        <div className="space-y-2 border-t border-border pt-2">
          {ev.outcome === 'pass' && (
            <label className="flex items-start gap-2 text-xs text-foreground">
              <Checkbox checked={seat} onCheckedChange={(v) => setSeat(v === true)} />
              <span>A seat on an allocated car is confirmed for him. His base salary is paid whatever the car earns.</span>
            </label>
          )}
          <Button
            variant={ev.outcome === 'pass' ? 'brand' : 'destructive'}
            disabled={busy || (ev.outcome === 'pass' && !seat)}
            onClick={doRecord}
          >
            {busy && <Spinner className="h-4 w-4" />}
            {ev.outcome === 'pass' ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {ev.outcome === 'pass' ? 'Record the offer' : 'Record: no offer'}
          </Button>
          <p className="text-xs text-muted-foreground">
            This records what the rule computed. It cannot record anything else.
          </p>
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, ok, need }: { label: string; value: string; ok: boolean; need: string }) {
  return (
    <div className="rounded-md border border-border px-2 py-1.5">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn('text-sm font-semibold tabular-nums', ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300')}>
        {value}
      </p>
      <p className="text-[11px] text-muted-foreground">needs {need}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Interview 2: the drive in its last 15 minutes. Migration 6800.
// ---------------------------------------------------------------------------

// The drive is scored with the same 0 to 3 rows as the interview, so a drive
// item is shown through QuestionRow as a rubric item with no N/A and no gate.
function driveAsRubricItem(d: DriveItem): RubricItem {
  return {
    stage: 'interview_2',
    code: d.code,
    part: 'drive',
    sort_order: d.sort_order,
    trait: null,
    question: d.question,
    school_version: null,
    guidance: d.guidance,
    anchor_3: d.anchor_3,
    anchor_2: d.anchor_2,
    anchor_1: d.anchor_1,
    anchor_0: d.anchor_0,
    min_score: 0,
    allows_na: false,
    knockout_at_zero: d.knockout_at_zero,
    must_not_be_zero: false,
    gate: null,
  }
}

function DriveForm({
  interviewId,
  items,
  drive,
  onChanged,
}: {
  interviewId: string
  items: DriveItem[]
  drive: DriveState
  onChanged: () => void
}) {
  const save = useSaveDriveScore()
  const submit = useSubmitDrive()
  const toAnswers = (a: DriveState['answers']): Record<string, Answer> =>
    Object.fromEntries(Object.entries(a ?? {}).map(([k, v]) => [k, { score: v.score, is_na: false, evidence: v.evidence }]))
  const [answers, setAnswers] = useState<Record<string, Answer>>(toAnswers(drive.answers))
  const [totals, setTotals] = useState<DriveTotals | null>(drive.totals ?? null)
  const [savingCode, setSavingCode] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setAnswers(toAnswers(drive.answers))
    setTotals(drive.totals ?? null)
  }, [drive.status, drive.answers])

  const readOnly = !drive.can_record
  const rows = useMemo(() => [...items].sort((a, b) => a.sort_order - b.sort_order).map(driveAsRubricItem), [items])

  const persist = useCallback(
    async (code: string, next: Answer) => {
      setAnswers((a) => ({ ...a, [code]: next }))
      setSavingCode(code)
      try {
        const res = (await save.trigger({
          interview_id: interviewId,
          code,
          score: next.score,
          evidence: next.evidence,
        })) as { totals: DriveTotals } | null
        if (res) setTotals(res.totals)
      } catch (err) {
        toast.error(`${code} was not saved`, { description: errText(err) })
      } finally {
        setSavingCode(null)
      }
    },
    [save.trigger, interviewId],
  )

  const doSubmit = async () => {
    setSubmitting(true)
    try {
      const res = (await submit.trigger({ interview_id: interviewId })) as { label?: string } | null
      toast.success(res?.label ?? 'Drive submitted')
      setConfirming(false)
      onChanged()
    } catch (err) {
      toast.error('Drive not submitted', { description: errText(err) })
    } finally {
      setSubmitting(false)
    }
  }

  const missing = totals?.missing ?? rows.map((r) => r.code)
  const needsEvidence = totals?.needs_evidence ?? []
  const ready = totals !== null && missing.length === 0 && needsEvidence.length === 0

  return (
    <div className="space-y-2">
      {rows.map((item) => (
        <QuestionRow
          key={item.code}
          item={item}
          answer={answers[item.code]}
          readOnly={readOnly}
          saving={savingCode === item.code}
          evidencePlaceholder="What you saw, in a line. Needed for a 0 or a 3."
          onScore={(score) => persist(item.code, { score, is_na: false, evidence: answers[item.code]?.evidence ?? null })}
          onEvidence={(text) =>
            persist(item.code, { score: answers[item.code]?.score ?? null, is_na: false, evidence: text || null })
          }
        />
      ))}
      {!readOnly && (
        <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card p-3 shadow-sm">
          <div className="text-sm">
            <span className="font-semibold tabular-nums">
              {totals ? `${totals.total} of ${totals.max}` : `0 of ${rows.length * 3}`}
            </span>
            <span className="ml-1 text-muted-foreground">(passes on {drive.pass_total ?? '?'})</span>
            <span className="ml-2 text-xs text-muted-foreground">
              {missing.length > 0 ? `${missing.length} still to score` : 'Every item scored'}
              {needsEvidence.length > 0 ? `. Note needed on ${needsEvidence.join(', ')}` : ''}
              {totals && totals.knockouts.length > 0 ? `. 0 on ${totals.knockouts.join(', ')} fails the drive` : ''}
            </span>
          </div>
          <Button variant="brand" disabled={!ready || submitting} onClick={() => setConfirming(true)}>
            <Car className="h-4 w-4" /> Submit drive
          </Button>
        </div>
      )}

      <Dialog open={confirming} onOpenChange={(o) => !o && !submitting && setConfirming(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit the drive?</DialogTitle>
            <DialogDescription>
              The drive scores lock when you submit. It passes on {drive.pass_total} of {totals?.max ?? rows.length * 3} with
              no 0 on a knockout item, and a failed drive means no offer, however the interview went.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={submitting}>
              Keep scoring
            </Button>
            <Button variant="brand" onClick={doSubmit} disabled={submitting}>
              {submitting && <Spinner className="h-4 w-4" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function DriveSection({
  block,
  data,
  onChanged,
}: {
  block: InterviewBlock
  data: ScoringData
  onChanged: () => void
}) {
  const drive = block.drive
  const [open, setOpen] = useState(false)
  if (!drive || !drive.required) return null
  const items = data.drive_rubric?.items ?? []
  const submitted = drive.status === 'submitted'
  const tone: ChipTone = !submitted ? 'amber' : drive.pass == null ? 'neutral' : drive.pass ? 'green' : 'red'
  const canSeeAnswers = !!drive.answers && Object.keys(drive.answers).length > 0

  return (
    <div className="space-y-2 rounded-md border border-border bg-background p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h5 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <Car className="h-4 w-4" /> Drive: the last {drive.minutes ?? 15} minutes
        </h5>
        <Chip tone={tone}>{drive.label}</Chip>
      </div>
      <p className="text-xs text-muted-foreground">
        Recorded by the manager on this interview{drive.recorded_by ? `, ${drive.recorded_by}` : ''}, and a pass or fail
        of its own beside the interview score.
      </p>
      {drive.can_record && data.drive_rubric?.intro && (
        <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">{data.drive_rubric.intro}</p>
      )}
      {(drive.can_record || (submitted && canSeeAnswers)) && (
        <Button size="sm" variant={open ? 'outline' : drive.can_record ? 'brand' : 'outline'} onClick={() => setOpen(!open)}>
          <Car className="h-4 w-4" />
          {open ? 'Hide the drive' : drive.can_record ? (drive.status === 'draft' ? 'Continue the drive' : 'Start the drive') : 'View the drive'}
        </Button>
      )}
      {open && <DriveForm interviewId={block.interview_id} items={items} drive={drive} onChanged={onChanged} />}
    </div>
  )
}

// The second scorer steps out of an Interview 2, which is then decided on the
// manager's scorecard alone. Migration 6801.
function SitOutButton({ block, onChanged }: { block: InterviewBlock; onChanged: () => void }) {
  const sitOut = useSitOutInterview()
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const mineDraft = block.scorecards.some((c) => c.mine && c.status === 'draft')

  const doSitOut = async () => {
    setBusy(true)
    try {
      await sitOut.trigger({ interview_id: block.interview_id, reason: reason.trim() || null })
      toast.success(`You sat out. ${block.manager_name ?? 'The manager'} scores this interview alone.`)
      setConfirming(false)
      onChanged()
    } catch (err) {
      toast.error('Not sat out', { description: errText(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
        <UserMinus className="h-4 w-4" /> Sit out this interview
      </Button>
      <Dialog open={confirming} onOpenChange={(o) => !o && !busy && setConfirming(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sit out this interview?</DialogTitle>
            <DialogDescription>
              It will be decided on {block.manager_name ?? 'the manager'}'s scorecard alone, with the drive as usual. You
              come off the interview and cannot score it afterwards.
              {mineDraft ? ' The scores you have started are set aside and kept on the record, not counted.' : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="sit-out-reason">Why, if you want to say (optional)</Label>
            <Input id="sit-out-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Off sick" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy}>
              Stay on it
            </Button>
            <Button variant="brand" onClick={doSitOut} disabled={busy}>
              {busy && <Spinner className="h-4 w-4" />}
              Sit out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ---------------------------------------------------------------------------
// One interview round
// ---------------------------------------------------------------------------

function RoundScoring({
  block,
  data,
  onChanged,
  defaultOpen = false,
}: {
  block: InterviewBlock
  data: ScoringData
  onChanged: () => void
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const mine = block.scorecards.find((c) => c.mine) ?? null
  const others = block.scorecards.filter((c) => !c.mine)
  const closed = !!block.completed_at || !!block.canceled_at
  const maxScorers = block.stage === 'interview_1' ? 1 : block.scorers_required ?? 2
  const roomForMe = mine !== null || block.scorecards.length < maxScorers
  // An applicant can be yours on Interview 1 and not on Interview 2, so the
  // applicant-level can_score is not enough for a round. `mine` absent means a
  // pre-0270 response, where can_score was the whole answer.
  const mayScoreRound = data.can_score && block.mine !== false
  const canScoreHere = mayScoreRound && !closed && roomForMe
  const scorers = scorerNames(block.stage, block.manager_name, block.second_interviewer_name)

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-foreground">{STAGE_TITLE[block.stage]}</h4>
          <p className="text-xs text-muted-foreground">
            {block.manager_name ? `Assigned to ${block.manager_name}. ` : ''}
            {block.stage === 'interview_2'
              ? block.scorers_required === 1
                ? `One scorer, ${block.manager_name ?? 'the manager'}, who asks every question.${block.sat_out?.name ? ` ${block.sat_out.name} sat out${block.sat_out.reason ? `: ${block.sat_out.reason}` : ''}.` : ''}`
                : 'Two scorers: the Driver Servant and Luyanda. The Driver Servant asks the odd numbered questions in each part, Luyanda the even.'
              : 'One scorer. Submitting records the outcome from the score.'}
          </p>
        </div>
        {block.decision ? (
          <Chip tone={block.decision.outcome === 'pass' ? 'green' : 'red'}>
            {block.decision.outcome === 'pass' ? 'Passed on the score' : 'Did not pass on the score'}
          </Chip>
        ) : block.canceled_at ? (
          <Chip tone="neutral">Cancelled</Chip>
        ) : block.completed_at ? (
          <Chip tone="neutral">Closed without a scorecard</Chip>
        ) : null}
      </div>

      {block.scorecards.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs">
          {block.scorecards.map((c) => (
            <Chip key={c.scorecard_id} tone={c.status === 'submitted' ? 'green' : 'amber'}>
              {c.mine ? 'You' : c.scorer_name}: {c.status === 'submitted' ? 'submitted' : 'scoring'}
              {c.visible !== false && c.totals && c.status === 'submitted' ? ` · ${c.totals.total} of ${c.totals.max}` : ''}
            </Chip>
          ))}
        </div>
      )}

      {(canScoreHere || block.can_sit_out) && (
        <div className="flex flex-wrap gap-2">
          {canScoreHere && (
            <Button size="sm" variant={open ? 'outline' : 'brand'} onClick={() => setOpen(!open)}>
              <ClipboardCheck className="h-4 w-4" />
              {open ? 'Hide my scorecard' : mine ? (mine.status === 'submitted' ? 'View my scorecard' : 'Continue scoring') : 'Start scoring'}
            </Button>
          )}
          {block.can_sit_out && <SitOutButton block={block} onChanged={onChanged} />}
        </div>
      )}
      {!mayScoreRound && !closed && (
        <p className="text-xs text-muted-foreground">
          {scorers.length > 0
            ? `Only ${scorers.join(' and ')} can score this interview.`
            : 'Nobody is assigned to this interview yet, so nobody can score it. Assign a manager first.'}
        </p>
      )}
      {mayScoreRound && !closed && !roomForMe && (
        <p className="text-xs text-muted-foreground">
          {block.stage === 'interview_1'
            ? `${others[0]?.scorer_name ?? 'Someone'} is scoring this interview.`
            : 'This interview already has its two scorers.'}
        </p>
      )}

      {open && canScoreHere && (
        <ScorecardForm
          stage={block.stage}
          data={data}
          interviewId={block.interview_id}
          refereeId={null}
          card={mine}
          revisable={block.stage === 'interview_2' && !block.decision && !closed}
          onChanged={onChanged}
        />
      )}

      {block.stage === 'interview_2' && <DriveSection block={block} data={data} onChanged={onChanged} />}
      {block.stage === 'interview_2' && <CompareTable data={data} block={block} onChanged={onChanged} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

// focusInterviewId: the Today view opens the panel for one interview. Only that
// round is shown, its scorecard opens straight away so the script and the
// questions are the first thing on screen, and Interview 2 brings its reference
// calls and the decision with it.
export function InterviewScoringPanel({
  applicationId,
  onOutcomeRecorded,
  focusInterviewId,
}: {
  applicationId: string
  onOutcomeRecorded?: () => void
  focusInterviewId?: string
}) {
  const get = useGetInterviewScoring<ScoringData>()

  const refresh = useCallback(() => {
    get.trigger({ application_id: applicationId }, { skipCache: true }).catch(() => {})
  }, [get.trigger, applicationId])

  useEffect(() => {
    refresh()
  }, [refresh])

  const changed = useCallback(() => {
    refresh()
    onOutcomeRecorded?.()
  }, [refresh, onOutcomeRecorded])

  const data = get.data
  if (get.error && !data) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>The scorecards could not be loaded. {get.error}</span>
      </div>
    )
  }
  if (!data) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Loading scorecards…
      </div>
    )
  }

  const blocks = data.interviews.filter(
    (b) => !b.canceled_at && (!focusInterviewId || b.interview_id === focusInterviewId),
  )
  const hasI2 = blocks.some((b) => b.stage === 'interview_2')

  if (blocks.length === 0) {
    return <p className="text-sm text-muted-foreground">No interview is booked yet, so there is nothing to score.</p>
  }

  return (
    <div className="space-y-4">
      {blocks.map((b) => (
        <RoundScoring
          key={b.interview_id}
          block={b}
          data={data}
          onChanged={changed}
          defaultOpen={b.interview_id === focusInterviewId}
        />
      ))}
      {hasI2 && <ReferencesSection applicationId={applicationId} data={data} onChanged={changed} />}
      {hasI2 && <DecisionSection applicationId={applicationId} data={data} onChanged={changed} />}
      <p className="text-[11px] text-muted-foreground">Rubric {data.rubric.version}.</p>
    </div>
  )
}
